import type { StepSubscriptions } from "@stepfinance/common";
import { StepSubscriptionsIDL } from "@stepfinance/common";
import type { TxHistoryTier } from "@stepfinance/transaction-history";
import { useQuery } from "@tanstack/react-query";

import { SUBSCRIPTIONS_PROGRAM_ID } from "../../../actions/step/tiers/constants";
import { useEnrichedWallet } from "../../../contexts/enrichedWallet";
import { useAnchorProgram } from "../../useAnchorProgram";
import { queryConfig } from "../queryConfig";

export const useUserTier = (selectedYear: number | string, tiers?: TxHistoryTier[]) => {
  const { publicKey } = useEnrichedWallet();
  const program = useAnchorProgram<StepSubscriptions>(StepSubscriptionsIDL, SUBSCRIPTIONS_PROGRAM_ID);

  const fetchUserTier = async () => {
    if (publicKey && program && tiers) {
      // Get all tiers that the user purchased/upgraded
      const purchaseAccounts = await program.account.purchase.all([
        {
          memcmp: {
            offset: 8 + 1,
            bytes: publicKey?.toBase58() ?? "",
          },
        },
      ]);
      if (purchaseAccounts.length === 0) {
        return undefined;
      }
      // Convert all purchases to tiers
      const formattedPurchaseAccounts: TxHistoryTier[] = [];
      for (const account of purchaseAccounts) {
        const productKey = account.account.product.toBase58();
        for (const tier of tiers) {
          if (productKey === tier.publicKey) {
            formattedPurchaseAccounts.push(tier);
            // Needed step to later do calculations with how cheaper is upgrading a tier due to already having bought one
            const amountPaidForTier = account.account.history10[0].amountPaid.toString();
            formattedPurchaseAccounts[formattedPurchaseAccounts.length - 1].totalPaid = amountPaidForTier;
          }
        }
      }
      let biggestTier: TxHistoryTier | undefined = undefined;
      // Find which purchases is the biggest tier
      for (const tier of formattedPurchaseAccounts) {
        if (!biggestTier || (biggestTier && tier.tierLevel > biggestTier.tierLevel)) {
          biggestTier = tier;
        }
      }

      return biggestTier;
    }
  };

  return useQuery<TxHistoryTier | undefined>(["user-tier", publicKey, selectedYear], fetchUserTier, {
    ...queryConfig,
    enabled: publicKey !== null && tiers !== undefined && tiers.length > 0,
    refetchOnWindowFocus: false,
  });
};
